import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { fetchUserModules as getUserModules, UserModule } from '../pages/my-methods/api/methodsApi';
import useThrowAsyncError from '../hooks/useThrowAsyncError';
import IdentityContext from './IdentityContext';

type UserModuleContextType = {
  userModules: UserModule[];
  isLoading: boolean;
  refetchUserModules: () => Promise<UserModule[]>;
};

const defaultUserModuleContext: UserModuleContextType = {
  userModules: [],
  isLoading: false,
  refetchUserModules: async () => [],
};

const UserModuleContext = createContext<UserModuleContextType>(defaultUserModuleContext);

export default UserModuleContext;

export const UserModuleContextProvider = ({ children }: { children: ReactNode }) => {
  const [userModules, setUserModules] = useState<UserModule[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { REACT_APP_P_STUDIO_GROUP } = process.env;
  const handleError = useThrowAsyncError();
  const { identity } = useContext(IdentityContext);

  const fetchUserModules = useCallback(async () => {
    try {
      const fetchedModules: UserModule[] = await getUserModules(REACT_APP_P_STUDIO_GROUP);
      setUserModules(fetchedModules);
      return fetchedModules;
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);
    }
    return [];
  }, [REACT_APP_P_STUDIO_GROUP, handleError]);

  useEffect(() => {
    if (identity.isAuthenticated) {
      setIsLoading(true);
      fetchUserModules();
    }
  }, [fetchUserModules, identity]);

  return (
    <UserModuleContext.Provider value={{ userModules, isLoading, refetchUserModules: fetchUserModules }}>
      {children}
    </UserModuleContext.Provider>
  );
};
